import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../interceptors/api";
import { useLayout } from "../../../LayoutContext";
import { placeHolderStories, UserStoryDto } from "../../dto/UserStoryDto";
import IPodLoadingOverlay from "./IPodLoadingOverlay";
import FinderWindow from "./FinderWindow";
import "./styles/IPodDesktopShell.css";
import IPodMiniPlayerWindow from "./IPodMiniPlayerWindow";
import TextEditStoryWindow from "./TextEditStoryWindow";

const BOOT_TIME = 4000; // 5s splash
const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

function IpodDesktopShell() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booted, setBooted] = useState(false);
  const [stories, setStories] = useState<UserStoryDto[]>([]);
  const [selectedStoryId, setSelectedStoryId] = useState<number | null>(null);
  const { setExhibitionMode } = useLayout();

  // inactivity & initial load
  const inactivityTimer = useRef<number>();
  const initialStoriesLoaded = useRef(false);

  const shareStory: UserStoryDto = {
    id: 99999,
    titleEN: "Share your iPod story!",
    titleDE: "Teile Deine iPod-Geschichte!",
    visualUrl: "/assets/themes/ipod/ipod_share-story_cover.png",
    textDE:
      "Teile Deine persönliche iPod-Geschichte und hilf der Erzählung zu wachsen!",
    textEN: "Share your personal iPod story help the tale grow!",
  };

  const resetTimer = useCallback(() => {
    window.clearTimeout(inactivityTimer.current);
    inactivityTimer.current = window.setTimeout(() => {
      navigate("/exhibits/werkschau", { replace: true });
    }, INACTIVITY_MS);
  }, [navigate]);

  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setLoading(false);
      setBooted(true);
    }, BOOT_TIME);
    return () => {
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (!booted || loading) return;

    const events = ["mousemove", "mousedown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer(); // start the timer immediately

    return () => {
      window.clearTimeout(inactivityTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [booted, loading, resetTimer]);

  useEffect(() => {
    let isMounted = true;

    const fetchStories = () => {
      api
        .get<UserStoryDto[]>("/api/user-story/device/2")
        .then((res) => {
          if (!isMounted) return;
          const userStories: UserStoryDto[] = [
            shareStory,
            ...res.data,
            /*...placeHolderStories,*/
          ];
          setStories(userStories);

          if (!initialStoriesLoaded.current) {
            setSelectedStoryId(userStories[1]?.id ?? null);
            initialStoriesLoaded.current = true;
          }
        })
        .catch((err) => {
          console.error("Failed to load stories:", err);
        });
    };

    fetchStories();
    const interval = window.setInterval(fetchStories, 60_000);

    return () => {
      isMounted = false;
      window.clearTimeout(interval);
    };
  }, []);

  const handleBackToStart = () => {
    navigate("/exhibits/werkschau", { replace: true });
  };

  if (loading) {
    return <IPodLoadingOverlay bootTime={BOOT_TIME} />;
  }

  return (
    <div className="ipod-desktop-shell">
      {/* Top bar with our “Back” icon */}
      <div className="ipod-top-bar"></div>

      {/* Main content area: split layout */}
      <div className="ipod-main-area">
        <div className="ipod-left-pane">
          <FinderWindow
            stories={stories}
            selectedId={selectedStoryId!}
            onSelect={setSelectedStoryId}
          />
          <IPodMiniPlayerWindow />
        </div>

        <div className="ipod-right-pane">
          <div className="ipod-desktop-icon-home" onClick={handleBackToStart}>
            <img
              src="/assets/themes/ipod/macosx-103_home_icon.png"
              alt="Home"
            />
            <span>Back to Welcome Screen</span>
          </div>

          <TextEditStoryWindow
            story={
              stories.find((userStory) => userStory.id === selectedStoryId)!
            }
          />
          <div className="ipod-qr-window"></div>
        </div>
      </div>
    </div>
  );
}

export default IpodDesktopShell;
